import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { contactsApi, faxImportApi } from "@/api";
import type { Source, ContactStatus } from "@/types";
import { Search, ChevronLeft, ChevronRight, Download, Upload } from "lucide-react";

const sourceLabels: Record<Source, string> = {
  TRADER: "取引先",
  TRADER_CONTACT: "担当者",
  MEMBER: "会員",
  FAX_PROSPECT: "FAXリスト",
};

const statusLabels: Record<ContactStatus, string> = {
  ACTIVE: "有効",
  UNSUBSCRIBED: "配信停止",
  BOUNCED: "バウンス",
};

const statusColors: Record<ContactStatus, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  UNSUBSCRIBED: "bg-slate-100 text-slate-600",
  BOUNCED: "bg-red-100 text-red-700",
};

export default function Contacts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sortField, setSortField] = useState("leadScore");
  const [sortDir, setSortDir] = useState("desc");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number; skipped: number; merged: number; total: number; errors: string[];
  } | null>(null);
  const csvFileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["contacts", { search, page, status: statusFilter, source: sourceFilter, sortField, sortDir }],
    queryFn: () => {
      const params: Record<string, string> = { page: String(page), limit: "50", sort: sortField, dir: sortDir };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      return contactsApi.list(params);
    },
  });

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">コンタクト</h2>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="メール, 会社名, 名前で検索..."
            className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-md text-sm"
        >
          <option value="">全ソース</option>
          <option value="TRADER">取引先</option>
          <option value="TRADER_CONTACT">担当者</option>
          <option value="MEMBER">会員</option>
          <option value="FAX_PROSPECT">FAXリスト</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-md text-sm"
        >
          <option value="">全ステータス</option>
          <option value="ACTIVE">有効</option>
          <option value="UNSUBSCRIBED">配信停止</option>
          <option value="BOUNCED">バウンス</option>
        </select>
        {data && (
          <span className="text-sm text-slate-500">{data.total}件</span>
        )}
        <a
          href={`/api/contacts/export?${new URLSearchParams({
            ...(search ? { search } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(sourceFilter ? { source: sourceFilter } : {}),
          }).toString()}`}
          className="flex items-center gap-1 px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
        >
          <Download size={14} />
          CSV
        </a>
        <button
          onClick={() => csvFileRef.current?.click()}
          disabled={importing}
          className="flex items-center gap-1 px-3 py-2 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          <Upload size={14} />
          {importing ? "インポート中..." : "FAXリスト"}
        </button>
        <input
          ref={csvFileRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            e.target.value = "";
            setImporting(true);
            setImportResult(null);
            try {
              const result = await faxImportApi.upload(file);
              setImportResult(result);
              queryClient.invalidateQueries({ queryKey: ["contacts"] });
            } catch (err: any) {
              alert(err.message || "Import failed");
            } finally {
              setImporting(false);
            }
          }}
        />
      </div>

      {/* Import result */}
      {importResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium">インポート完了:</span>{" "}
              作成 <span className="font-bold text-green-600">{importResult.created}</span>件 /
              マージ <span className="font-bold text-blue-600">{importResult.merged}</span>件 /
              スキップ <span className="text-slate-500">{importResult.skipped}</span>件 /
              合計 {importResult.total}件
            </div>
            <button onClick={() => setImportResult(null)} className="text-slate-400 hover:text-slate-600 text-xs">
              閉じる
            </button>
          </div>
          {importResult.errors.length > 0 && (
            <div className="mt-2 text-xs text-red-600">
              {importResult.errors.slice(0, 5).map((e, i) => (
                <div key={i}>{e}</div>
              ))}
              {importResult.errors.length > 5 && (
                <div>...他 {importResult.errors.length - 5}件のエラー</div>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {[
                    { key: "email", label: "メール" },
                    { key: "company", label: "会社名" },
                    { key: "", label: "FAX番号" },
                    { key: "", label: "ソース" },
                    { key: "", label: "取引先ランク" },
                    { key: "", label: "RFM" },
                    { key: "leadScore", label: "スコア" },
                    { key: "", label: "ステータス" },
                  ].map(({ key, label }) => (
                    <th
                      key={label}
                      className={`text-left px-4 py-2 font-medium text-slate-600 ${key ? "cursor-pointer hover:text-blue-600 select-none" : ""}`}
                      onClick={key ? () => {
                        if (sortField === key) {
                          setSortDir(d => d === "desc" ? "asc" : "desc");
                        } else {
                          setSortField(key);
                          setSortDir("desc");
                        }
                        setPage(1);
                      } : undefined}
                    >
                      {label}
                      {sortField === key && <span className="ml-1">{sortDir === "desc" ? "▼" : "▲"}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/contacts/${c.id}`)}>
                    <td className="px-4 py-2">{c.email}</td>
                    <td className="px-4 py-2 text-slate-600">{c.company || "-"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{c.faxNumber || "-"}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs">{sourceLabels[c.source]}</span>
                    </td>
                    <td className="px-4 py-2">{c.traderRank || "-"}</td>
                    <td className="px-4 py-2">{c.rfmRank || "-"}</td>
                    <td className="px-4 py-2">{c.leadScore}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${statusColors[c.status]}`}>
                        {statusLabels[c.status]}
                      </span>
                    </td>
                  </tr>
                ))}
                {data?.data.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                      コンタクトが見つかりません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {(page - 1) * data.limit + 1}-{Math.min(page * data.limit, data.total)} / {data.total}件
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page >= data.totalPages}
                  className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
